
import Vue from 'vue';
import VueRouter from 'vue-router';
import { loadDocumentation } from './utils/loader';

import PageDemoCsl from './pages/page-demo-csl.vue';
import PageDocs from './pages/page-docs.vue';
import PageCdm from './pages/page-cdm.vue';
import PageSearch from './pages/page-search.vue';
import PageIeFallback from './pages/page-ie-fallback.vue';
import PageDemoPlayer from './pages/page-demo-player.vue';
import PageDevPlaylist from './pages/page-dev-playlist.vue';
import PageDemoSelect from './pages/page-demo-select.vue';
import PageDebug from './pages/page-debug.vue';
import PageLogin from './pages/page-login.vue';
import PlayerPlots from './components/player/PlayerPlots';
import PlayerLogs from './components/player/PlayerLogs';
import PlayerConfig from './components/player/PlayerConfig';
import { ENV } from './utils/env.js';

Vue.use(VueRouter);

const routes = [
  { path: '/', redirect: { name: 'player' } },
  {
    path: '/docs/api/:path?',
    component: PageDocs,
    name: 'docs/api',
    beforeEnter: (to, from, next) => {
      loadDocumentation().then(next, next);
    }
  },
  {
    path: '/docs/:path?',
    component: PageDocs,
    name: 'docs',
    beforeEnter: (to, from, next) => {
      loadDocumentation().then(next, next);
    }
  },
  {
    path: '/search',
    component: PageSearch,
    name: 'search',
    props: (route) => ({ query: route.query.q }),
    beforeEnter: (to, from, next) => {
      loadDocumentation().then(next, next);
    }
  },
  {
    path: '/login',
    component: PageLogin,
    name: 'login'
  },
  {
    path: '/player/',
    component: PageDemoPlayer,
    name: 'player',
    props: (route) => {
      let cfg = {};
      try {
        cfg = JSON.parse(atob(route.query.cfg || '') || '{}');
      } catch (e) {
        console.log('Failed to parse config', e);
      }
      return {
        cfg: cfg,
        assetId: route.query.assetId,
        customId: route.query.customId
      };
    },
    beforeEnter: (to, from, next) => {
      loadDocumentation().then(next, next);
    },
    children: [
      {
        path: 'demo',
        component: PageDemoSelect,
        name: 'player.demo',
        props: true
      },
      {
        path: 'stats',
        component: PlayerPlots,
        name: 'player.stats',
        alias: '',
        props: true
      },
      {
        path: 'logs',
        component: PlayerLogs,
        name: 'player.logs',
        props: true
      },
      {
        path: 'config',
        component: PlayerConfig,
        name: 'player.config',
        props: true
      }
    ]
  },
  {
    path: '/ie',
    component: PageIeFallback,
    name: 'ie'
  },
  {
    path: '/cdm',
    component: PageCdm,
    name: 'cdm'
  },
  {
    path: '/demos/csl',
    component: PageDemoCsl,
    name: 'csl'
  },
];

if (ENV.IS_DEVELOPMENT) {
  routes.push({
    path: '/debug',
    component: PageDebug,
    name: 'debug'
  });
  routes.push({
    path: '/dev/playlist',
    component: PageDevPlaylist,
    name: 'dev-playlist'
  });
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  scrollBehavior: function (to, from, savedPosition) {
    const top = {
      x: 0,
      y: 0
    };

    const { name, hash } = to;

    if (name && name.indexOf('player') === 0) return top;

    if (hash) {
      return new Promise((resolve) => {
        Vue.nextTick(() => {
          // TODO: Fix the scrolling to the position at initial app load
          resolve({ selector: hash });
        });
      }
      );
    } else {
      return top;
    }
  },
  routes: routes
});

// IE guard
router.beforeEach((to, from, next) => {
  const userAgent = navigator.userAgent || '';
  var isIe = userAgent.includes('Trident/');

  if (to.name !== 'ie' && isIe) {
    next({ name: 'ie' });
  } else if (to.name === 'ie' && !isIe) {
    next({ name: 'player' });
  } else {
    next();
  }
});

// For backwards compatibility with history router, see examples bellow.

router.beforeEach((to, _, next) => {
  if (to.hash && to.hash.startsWith('#/docs?q=')) {
    // E.g.: https://demo.castlabs.com/#/docs?q=clpp.Player
    const queryString = to.hash.substring(9);
    const newPath = `/docs/api/${queryString}`;
    next({ path: newPath, replace: true });
  } else if (to.hash && to.hash.startsWith('#/')) {
    // E.g.: https://demo.castlabs.com/#/docs/getting_started
    const path = to.hash.substring(1);
    next({ path: path, replace: true });
  } else {
    next();
  }
});

/**
 * Navigates to a path using Vue router.
 * Returns always false to prevent default behaviour of
 * onclick event in anchor tag - <a>.
 *
 * @param {string} path
 * @return {boolean}
 */
function pushRouterPath (path) {
  router.push({ path: path });
  return false;
}

window.pushRouterPath = pushRouterPath;

export { router };
